import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { Science as ScienceIcon } from '@mui/icons-material';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const ProcessTissueDialog = ({ open, onClose, donor, onSuccess }) => {
  const [processingNotes, setProcessingNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawMaterials, setRawMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);

  const handleClose = () => {
    setProcessingNotes('');
    setError('');
    setRawMaterials([]);
    onClose();
  };

  // Fetch raw materials used for this donor
  useEffect(() => {
    if (open && donor?.id) {
      const fetchRawMaterials = async () => {
        try {
          setMaterialsLoading(true);
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/raw-materials`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setRawMaterials(data.data || []);
          }
        } catch (err) {
          console.error('Error fetching raw materials:', err);
        } finally {
          setMaterialsLoading(false);
        }
      };

      fetchRawMaterials();
    }
  }, [open, donor?.id]);

  // Calculate total cost
  const totalCost = rawMaterials.reduce((sum, material) => {
    const costPerItem = Number(material.costPerItem) || 0;
    const quantityUsed = Number(material.quantityUsed) || 0;
    return sum + (costPerItem * quantityUsed);
  }, 0);

  const formatDate = (date) => {
    if (!date) return '-';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString();
  };

  const handleFinishProcessing = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/finish-processing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: processingNotes })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete processing');
      }

      handleClose();
      onSuccess(data.data);
    } catch (err) {
      console.error('Error finishing processing:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!donor) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Process Tissue</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {donor.donorId} - {donor.mareName}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Donor Info Summary */}
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Processing Donor</Typography>
            <Typography variant="h6" fontWeight="bold">{donor.donorId}</Typography>
            <Typography variant="body2">{donor.mareName}</Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              {donor.tissues?.map((t) => (
                <Chip 
                  key={t.id} 
                  label={t.tissueType === 'AM' ? 'Amniotic Membrane' : 'Umbilical Cord'} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Raw Materials Used */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Raw Materials Used
          </Typography>
          
          {materialsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : rawMaterials.length === 0 ? (
            <Alert severity="info">
              No raw materials have been recorded for this donor yet. You can add them from the donor detail page.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Item Description</strong></TableCell>
                      <TableCell><strong>Lot Number</strong></TableCell>
                      <TableCell align="right"><strong>Quantity</strong></TableCell>
                      <TableCell align="right"><strong>Cost/Unit</strong></TableCell>
                      <TableCell align="right"><strong>Total Cost</strong></TableCell>
                      <TableCell><strong>Date Used</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rawMaterials.map((material) => {
                      const costPerItem = Number(material.costPerItem) || 0;
                      const quantityUsed = Number(material.quantityUsed) || 0;
                      const lineTotal = costPerItem * quantityUsed;
                      
                      return (
                        <TableRow key={material.id}>
                          <TableCell>{material.itemDescription || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{material.lotNumber || '-'}</TableCell>
                          <TableCell align="right">{quantityUsed}</TableCell>
                          <TableCell align="right">${costPerItem.toFixed(2)}</TableCell>
                          <TableCell align="right"><strong>${lineTotal.toFixed(2)}</strong></TableCell>
                          <TableCell>{formatDate(material.dateUsed)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', minWidth: 200 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Cost
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${totalCost.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </>
          )}
        </Box>

        {/* Processing Notes */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Processing Notes
        </Typography>
        <TextField
          multiline
          rows={4}
          value={processingNotes}
          onChange={(e) => setProcessingNotes(e.target.value)}
          fullWidth
          placeholder="Enter any notes about the processing..."
          sx={{ mb: 2 }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleFinishProcessing} 
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          color="success"
        >
          {loading ? 'Processing...' : 'Finish Processing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessTissueDialog;

